/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useMemo } from 'react';

import { Channel } from '../../../../core/entities';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2 } from '../../../../core/types';
import { getRecordingsStatus } from '../../../../store/recordings/recordings.reducers';
import { Mousetrap } from '../../../common';
import { FILTERED_GUIDE_MAX_SHOWS_PER_PAGE } from '../filteredguide.constants';
import FilteredGuideInvisibleRow from './FilteredGuideInvisibleRow/FilteredGuideInvisibleRow';
import {
    getFirstProgramStartTimeFromPage,
    getTurboScrollTimeText,
    LimitedGuideProgramResult,
} from './filteredguidetable.utils';
import FilteredGuideTableHeader from './FilteredGuideTableHeader/FilteredGuideTableHeader';
import FilteredGuideTableRow from './FilteredGuideTableRow/FilteredGuideTableRow';
import FilteredGuideTableSeparatorRow from './FilteredGuideTableSeparatorRow/FilteredGuideTableSeparatorRow';
import FilteredGuideTurboScrollRow from './FilteredGuideTurboScrollRow/FilteredGuideTurboScrollRow';

import './FilteredGuideTable.scss';

export interface IFilteredGuideTableProps {
    category: string;
    allPossibleChannels: Array<TChannelV2 | Channel>;
    currentPageData: LimitedGuideProgramResult[];
    pageIndex: number;
    isTurboScrollEnabled: boolean;
    turboScrollCurrentDisplayedDate: Date;
}

const FilteredGuideTableUI = ({
    category,
    allPossibleChannels,
    currentPageData,
    pageIndex,
    isTurboScrollEnabled,
    turboScrollCurrentDisplayedDate,
}: IFilteredGuideTableProps) => {
    const { maxAge, recordingsStatusMap } = useTivoSelector(
        (state: RootState) => state.recordingsStatus,
    );
    const dispatch = useTivoDispatch();

    const headerTimestamp = useMemo(() => {
        if (currentPageData.length === 0) return turboScrollCurrentDisplayedDate.getTime();
        const firstProgramStart = getFirstProgramStartTimeFromPage(currentPageData);
        return isTurboScrollEnabled
            ? turboScrollCurrentDisplayedDate.getTime()
            : firstProgramStart * 1000;
    }, [currentPageData, isTurboScrollEnabled, turboScrollCurrentDisplayedDate]);

    useEffect(() => {
        let recordingsStatusTimerId: NodeJS.Timeout;
        if (maxAge) {
            recordingsStatusTimerId = setTimeout(() => {
                dispatch(getRecordingsStatus());
            }, maxAge * 1000);
        }

        return () => {
            clearTimeout(recordingsStatusTimerId);
        };
    }, [recordingsStatusMap]);

    const createFilteredGuideRows = () => {
        let rowIndex = 0;
        const accRows: JSX.Element[] = [];

        if (isTurboScrollEnabled || currentPageData.length === 0) {
            for (let i = 0; i < FILTERED_GUIDE_MAX_SHOWS_PER_PAGE; i += 1) {
                accRows.push(<FilteredGuideTurboScrollRow key={`turbo-scroll-${i}`} />);
            }
            return accRows;
        }

        if (pageIndex < 0 && currentPageData.length !== FILTERED_GUIDE_MAX_SHOWS_PER_PAGE) {
            const noOfInvisibleRows = FILTERED_GUIDE_MAX_SHOWS_PER_PAGE - currentPageData.length;
            for (let i = 0; i < noOfInvisibleRows; i += 1) {
                accRows.push(
                    <Mousetrap value={`invisible-row-${i}`} key={`invisible-${i}`}>
                        <FilteredGuideInvisibleRow key={`invisible-${i}`} />
                    </Mousetrap>,
                );
            }
        }

        return currentPageData.reduce<JSX.Element[]>((acc, item, index) => {
            if (item.type === 'time') {
                acc.push(
                    <Mousetrap value={item.value} key={`time-mousetrap-${item.value}`}>
                        <FilteredGuideTableSeparatorRow
                            startTime={item.value}
                            key={`time-${item.value}`}
                        />
                    </Mousetrap>,
                );
            } else {
                acc.push(
                    <FilteredGuideTableRow
                        filteredGuideRow={item.value}
                        rowIndex={rowIndex}
                        allPossibleChannels={allPossibleChannels}
                        key={item.value.offerId + index}
                        recordingsStatusMap={recordingsStatusMap}
                    />,
                );
                rowIndex += 1;
            }
            return acc;
        }, accRows);
    };

    return (
        <>
            <FilteredGuideTableHeader category={category} firstChannelStartTime={headerTimestamp} />
            <div className="filtered-guide-content-body">
                {createFilteredGuideRows()}
                {isTurboScrollEnabled && (
                    <div className="filtered-guide-turbo-time">
                        {getTurboScrollTimeText(turboScrollCurrentDisplayedDate)}
                    </div>
                )}
            </div>
        </>
    );
};

export default FilteredGuideTableUI;
